import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Activity, Stethoscope, Newspaper, Users, Zap, TrendingUp, Timer, Gauge } from "lucide-react";

export function usePulse() {
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    const load = () => api.get("/home/pulse").then(({ data }) => setPulse(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return pulse;
}

function useRelTime(iso) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const s = Math.max(5, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} seconds ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function LiveNow({ pulse }) {
  const rel = useRelTime(pulse?.last_update);
  if (!pulse) return null;
  return (
    <div
      className="inline-flex items-center gap-2.5 sm:gap-3 rounded-full border border-titans-red/50 bg-titans-red/10 backdrop-blur-md px-4 py-2 pulse-ring"
      data-testid="hero-live-now"
    >
      <span className="live-dot" />
      <span className="font-cond uppercase tracking-[0.2em] text-[11px] text-titans-red font-bold">Live Now</span>
      <span className="font-cond uppercase tracking-wider text-[11px] text-white" data-testid="hero-live-label">{pulse.live_label}</span>
      {rel && (
        <span className="font-cond uppercase tracking-wider text-[10px] text-white/60 border-l border-white/20 pl-2.5 sm:pl-3" data-testid="hero-live-updated">
          Updated {rel}
        </span>
      )}
    </div>
  );
}

const GLASS = {
  background: "rgba(7,18,33,0.93)",
  backdropFilter: "blur(9px)",
  WebkitBackdropFilter: "blur(9px)",
  boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
};

export function FeaturedStory({ pulse }) {
  const f = pulse?.featured;
  if (!f) return null;
  return (
    <Link
      to={`/news/${f.slug || f.id}`}
      data-testid="hero-featured-story"
      className="block rounded-xl border border-white/10 p-4 sm:p-5 hover:border-titans-blue/60 transition-colors group"
      style={GLASS}
    >
      <div className="flex items-center gap-2 mb-2">
        <Newspaper size={12} className="text-titans-red" />
        <span className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-red">Featured Story</span>
        {f.category && <span className="font-cond uppercase tracking-wider text-[9px] text-white/50 ml-auto">{f.category}</span>}
      </div>
      <div className="font-display uppercase text-lg sm:text-xl text-white leading-[1.1] line-clamp-2 group-hover:text-titans-blue transition-colors">
        {f.headline}
      </div>
      <div className="mt-2.5 inline-flex items-center gap-1.5 font-cond uppercase tracking-wider text-[11px] text-titans-blue">
        Read the story <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function PulseRow({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5" data-testid={testid}>
      <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55">
        <Icon size={12} className={accent || "text-titans-blue"} /> {label}
      </span>
      <span className="font-display text-xl text-white leading-none tabular-nums">{value}</span>
    </div>
  );
}

export function TitansPulseCard({ pulse, nextGame, trending }) {
  if (!pulse) return null;
  let gameDays = null;
  let opp = null;
  if (nextGame?.date) {
    gameDays = Math.max(0, Math.ceil((new Date(nextGame.date).getTime() - Date.now()) / 86400000));
    opp = nextGame.away?.abbr === "TEN" ? nextGame.home?.abbr : nextGame.away?.abbr;
  }
  const f = trending || pulse.featured;
  return (
    <div className="rounded-xl border border-white/10 p-4 sm:p-5" style={GLASS} data-testid="titans-pulse-card">
      <div className="flex items-center gap-2 mb-1">
        <Activity size={13} className="text-titans-red" />
        <span className="font-cond uppercase tracking-[0.25em] text-[10px] text-white/80">Today's Titans Pulse</span>
        {pulse.camp_day && (
          <span className="ml-auto font-cond uppercase tracking-wider text-[9px] text-white/45" data-testid="pulse-camp-day">
            Camp Day {pulse.camp_day}
          </span>
        )}
      </div>
      <div className="divide-y divide-white/[0.07]">
        <PulseRow icon={Zap} label="Live Updates" value={pulse.updates_today} accent="text-titans-red" testid="pulse-updates" />
        <PulseRow icon={Stethoscope} label="Injury Watch" value={pulse.injuries_week} testid="pulse-injuries" />
        <PulseRow
          icon={Timer}
          label={opp ? `Next Game · ${opp}` : "Next Game"}
          value={gameDays !== null ? `${gameDays}d` : "TBD"}
          testid="pulse-next-game"
        />
        {f && (
          <Link to={`/news/${f.slug || f.id}`} className="flex items-center justify-between gap-3 py-2.5 group" data-testid="pulse-trending">
            <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55 shrink-0">
              <TrendingUp size={12} className="text-titans-blue" /> Trending
            </span>
            <span className="font-headline font-semibold text-[12px] text-white/90 leading-snug text-right line-clamp-1 group-hover:text-titans-blue transition-colors">
              {f.headline}
            </span>
          </Link>
        )}
        {typeof pulse.confidence === "number" && (
          <Link to="/community?tab=confidence" className="block py-2.5 group" data-testid="pulse-confidence">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55">
                <Gauge size={12} className="text-titans-blue" /> Fan Confidence
              </span>
              <span className="font-display text-xl text-white leading-none tabular-nums">{pulse.confidence}%</span>
            </div>
            <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-titans-blue" style={{ width: `${pulse.confidence}%` }} />
            </div>
            <div className="font-cond uppercase tracking-wider text-[8px] text-white/35 mt-1.5 group-hover:text-titans-blue transition-colors">Cast your vote →</div>
          </Link>
        )}
      </div>
    </div>
  );
}

export function HeroTicker({ pulse, nextGame }) {
  if (!pulse?.ticker?.length) return null;
  const items = pulse.ticker.map((t) => ({ label: t.source, text: t.text }));
  if (pulse.camp_day) items.unshift({ label: "Camp", text: `Training Camp · Day ${pulse.camp_day}` });
  if (nextGame?.date) {
    const dd = Math.max(0, Math.ceil((new Date(nextGame.date).getTime() - Date.now()) / 86400000));
    items.push({ label: "Next", text: `Next Titans game in ${dd} day${dd === 1 ? "" : "s"}` });
  }
  const loop = [...items, ...items];
  return (
    <div className="relative z-[2] border-b border-white/10 bg-black/45 backdrop-blur-sm overflow-hidden" data-testid="hero-ticker">
      <div className="flex items-center">
        <span className="shrink-0 relative z-10 bg-titans-red text-white font-cond uppercase tracking-[0.2em] text-[9px] px-3 py-2">The Wire</span>
        <div className="ticker-track py-1.5 pl-6">
          {loop.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-2 font-cond uppercase tracking-wider text-[10px] text-white/75 whitespace-nowrap">
              <span className="text-titans-red text-[7px]">●</span>
              <span className="text-titans-blue">{it.label}</span> {it.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SocialProof({ pulse }) {
  const items = [
    { icon: Users, text: `Join ${(pulse?.fans || 3000).toLocaleString()}+ Titans fans` },
    { icon: Zap, text: pulse?.updates_today > 3 ? `Updated ${pulse.updates_today}× today` : "Updated dozens of times daily" },
    { icon: TrendingUp, text: "Nashville's fastest-growing Titans community" },
  ];
  return (
    <div className="mt-8 md:mt-10 pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-7 gap-y-2.5" data-testid="hero-social-proof">
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-2 font-cond uppercase tracking-wider text-[10px] sm:text-[11px] text-white/60">
          <it.icon size={12} className="text-titans-blue" /> {it.text}
        </span>
      ))}
    </div>
  );
}
